// The map screen's controls, as URL search params, so "the pipeline for
// Harris" is a link somebody can send before a meeting. The currency is not one
// of them: it is the reader's report currency (`format/reportCurrency.tsx`). Same hardened shape as
// `regionsSearch.ts`: every field falls back alone, and the parse runs again at
// the point of use because the root route validates nothing.

import { z } from "zod";
import { ReportScope, Uuid } from "@crm/shared";
import type { MapScope } from "../../api/queries.js";
import { MAP_VIEWS, type MapView } from "./mapDeals.js";

const MapSearchSchema = z.object({
  view: z.enum(MAP_VIEWS).optional().catch(undefined),
  partner_id: Uuid.optional().catch(undefined),
  scope: ReportScope.optional().catch(undefined),
});

export type MapSearch = z.infer<typeof MapSearchSchema>;

export interface MapState extends MapScope {
  view: MapView;
}

export const DEFAULT_MAP_STATE: MapState = {
  view: "both",
  partnerId: null,
  scope: "rolled_up",
};

export function validateMapSearch(raw: Record<string, unknown>): MapSearch {
  const parsed = MapSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

export function searchToMapState(raw: MapSearch): MapState {
  const search = validateMapSearch(raw as Record<string, unknown>);
  return {
    view: search.view ?? DEFAULT_MAP_STATE.view,
    partnerId: search.partner_id ?? DEFAULT_MAP_STATE.partnerId,
    scope: search.scope ?? DEFAULT_MAP_STATE.scope,
  };
}

/** Defaults are omitted, so the default map is a clean `/map`. */
export function mapStateToSearch(state: MapState): MapSearch {
  return {
    ...(state.view === DEFAULT_MAP_STATE.view ? {} : { view: state.view }),
    ...(state.partnerId === null ? {} : { partner_id: state.partnerId }),
    // The scope only means something alongside a partner.
    ...(state.partnerId === null || state.scope === DEFAULT_MAP_STATE.scope
      ? {}
      : { scope: state.scope }),
  };
}
